import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { debounceTime, distinctUntilChanged, Subject, switchMap, of, catchError, takeUntil, map, Observable } from 'rxjs';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { EntitySelectComponent, EntitySelectPage } from '../../../shared/components/entity-select/entity-select.component';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';
import {
  DirectoryUser,
  UserDirectoryApiService,
  directoryUserLabel,
} from '../../../shared/services/user-directory-api.service';
import { MeetingBooksLinkApiService } from '../services/meeting-books-link-api.service';
import { MeetingTypeApiService } from '../services/meeting-type-api.service';
import { MeetingStatusApiService } from '../services/meeting-status-api.service';
import { MeetingBooksLinkRequest, MeetingStatusResponse, MeetingTypeResponse } from '../models/meeting.model';
import { CurrencyService } from '../../currency/currency.service';
import { CurrencyResponse } from '../../currency/models/currency.model';
import { DealApiService } from '../../deals/services/deal-api.service';
import { DealCycle } from '../../deals/models/deal.model';
import { BookingAvailabilityComponent } from '../booking-sub/availability/booking-availability.component';
import { BookingFormFieldsComponent } from '../booking-sub/form-fields/booking-form-fields.component';
import { BookingMembersComponent } from '../booking-sub/members/booking-members.component';
import { BookingReminderConfigsComponent } from '../booking-sub/reminder-configs/booking-reminder-configs.component';
import { BookingNurtureEmailsComponent } from '../booking-sub/nurture-emails/booking-nurture-emails.component';

// La duración de la reunión se define en General → "Slots y buffers" →
// "Duración por defecto (min)"; por eso ya no existe la pestaña 'durations'.
type BookingTab =
  | 'general'
  | 'availability'
  | 'form-fields'
  | 'members'
  | 'reminders'
  | 'nurture';

@Component({
  selector: 'app-booking-link-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    EntitySelectComponent,
    BookingAvailabilityComponent,
    BookingFormFieldsComponent,
    BookingMembersComponent,
    BookingReminderConfigsComponent,
    BookingNurtureEmailsComponent,
  ],
  templateUrl: './booking-link-form.component.html',
})
export class BookingLinkFormComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  isEditing = false;
  linkId: number | null = null;
  loading = false;
  saving = false;
  error = '';

  // Zonas horarias IANA válidas (INC 16) — el backend rechaza valores fuera de esta lista
  readonly timezones: string[] = Intl.supportedValuesOf('timeZone');

  activeTab: BookingTab = 'general';
  readonly tabs: { key: BookingTab; label: string }[] = [
    { key: 'general', label: 'General' },
    { key: 'availability', label: 'Disponibilidad' },
    { key: 'form-fields', label: 'Campos del form' },
    { key: 'members', label: 'Miembros' },
    { key: 'reminders', label: 'Recordatorios' },
    { key: 'nurture', label: 'Correos de seguimiento' },
  ];

  meetingTypes: MeetingTypeResponse[] = [];

  /** Monedas del sistema para el selector de pago (INC 53). */
  currencies: CurrencyResponse[] = [];

  /** Etapas del pipeline (deal cycles) para el selector de pipeline (INC 55). */
  dealCycles: DealCycle[] = [];

  /**
   * INC 70 — estados de reunión (Creado, Confirmado, Finalizado…) para el selector
   * "Estado de la reunión al reservar" (antes mostraba etapas del pipeline de negocios).
   */
  meetingStatuses: MeetingStatusResponse[] = [];

  /** Idiomas disponibles para el selector múltiple de "Idiomas soportados" (INC 52). */
  readonly languageOptions: { code: string; label: string }[] = [
    { code: 'es', label: 'Español' },
    { code: 'en', label: 'Inglés' },
    { code: 'pt', label: 'Português' },
  ];
  /** Códigos de idioma seleccionados; se serializa a JSON en supportedLanguages. */
  selectedLanguages: string[] = ['es'];

  /** Constructor visual de la agenda por defecto (INC 54); se serializa a defaultAgenda JSON. */
  agendaItems: { title: string; duration: number | null }[] = [];

  /** Nombre del organizador seleccionado (texto a mostrar en el buscador). */
  organizerName = '';

  /**
   * Canales de videollamada que entiende el backend (MeetingService).
   * 'jitsi' = sala integrada (el backend genera la URL); 'google'/'microsoft'/'zoom'
   * requieren la integración OAuth correspondiente; 'custom' usa customVideoUrl;
   * '' = sin video (reunión presencial o sin enlace).
   */
  readonly videoProviders: { value: string; label: string; hint: string; icon: string }[] = [
    { value: 'jitsi', label: 'Jitsi (integrado)', hint: 'Sala automática, sin configurar', icon: '🎥' },
    { value: 'google', label: 'Google Meet', hint: 'Requiere conectar Google', icon: '📹' },
    { value: 'microsoft', label: 'Microsoft Teams', hint: 'Requiere conectar Outlook', icon: '💬' },
    { value: 'zoom', label: 'Zoom', hint: 'Integración con Zoom', icon: '🔵' },
    { value: 'custom', label: 'URL personalizada', hint: 'Pega tu propio enlace', icon: '🔗' },
    { value: '', label: 'Sin video / presencial', hint: 'Reunión presencial o sin enlace', icon: '📍' },
  ];

  // Secciones colapsables
  openSection: Record<string, boolean> = {
    basic: true,
    scheduling: false,
    branding: false,
    video: true,
    location: false,
    payment: false,
    reschedule: false,
    confirmation: false,
    gdpr: false,
    embed: false,
    pipeline: false,
  };

  // Slug validation
  slugChecking = false;
  slugAvailable: boolean | null = null;
  private slugCheck$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private api: MeetingBooksLinkApiService,
    private meetingTypeApi: MeetingTypeApiService,
    private authService: AuthService,
    private userDirectory: UserDirectoryApiService,
    private currencyService: CurrencyService,
    private dealApi: DealApiService,
    private meetingStatusApi: MeetingStatusApiService,
    private toast: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.initForm();
  }

  // ─── Búsqueda de usuario por nombre/correo ───
  // Usa el directorio canónico (GET /api/directory/users), que solo exige estar AUTENTICADO.
  // Antes pegaba a /api/auth/users/search, gateado con `users:VIEW`: cualquier rol que no fuera
  // Super Admin / Admin / Manager recibía 403, el buscador se quedaba sin resultados y el nombre
  // del organizador degradaba a «Usuario #id».
  userSearch = (term: string): Observable<EntitySelectPage<DirectoryUser>> =>
    this.userDirectory.searchUsers(term, 20).pipe(
      map((items) => ({ items, totalPages: 1 })),
      catchError(() => of({ items: [] as DirectoryUser[], totalPages: 0 }))
    );

  userLabel = (u: DirectoryUser): string => directoryUserLabel(u);
  userSublabel = (u: DirectoryUser): string => u.mail ?? '';

  onOrganizerSelected(u: DirectoryUser | null): void {
    this.organizerName = u ? this.userLabel(u) : '';
  }

  ngOnInit(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.meetingTypeApi.listActive(businessId).subscribe({
      next: (types) => (this.meetingTypes = types),
      error: () => {},
    });

    // Monedas del sistema para el selector de pago (INC 53).
    this.currencyService.getCurrencies().subscribe({
      next: (c) => (this.currencies = c ?? []),
      error: () => {},
    });

    // Etapas del pipeline (deal cycles) para el selector de pipeline (INC 55).
    this.dealApi.getDealCycles().subscribe({
      next: (cycles) => (this.dealCycles = cycles ?? []),
      error: () => {},
    });

    // INC 70 — estados de reunión para el selector "Estado de la reunión al reservar".
    this.meetingStatusApi.listActive(businessId).subscribe({
      next: (statuses) => (this.meetingStatuses = statuses ?? []),
      error: () => {},
    });

    this.setupSlugCheck();
    if (user?.id) {
      this.form.patchValue({ idUser: user.id });
      // Por defecto el organizador es el usuario actual (puede cambiarse buscando otro).
      const nombre = `${user.fullname ?? ''} ${user.lastname ?? ''}`.trim();
      this.organizerName = nombre || user.email || '';
      // Sesión sin nombre en `current_user` (login antiguo en localStorage): se pide al
      // directorio en vez de dejar el campo vacío o con el id a la vista.
      if (!this.organizerName) this.resolverOrganizador(user.id);
    }

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.linkId = +id;
      this.cargar(this.linkId);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.form = this.fb.group({
      // Obligatorios
      idBussines: [null, Validators.required],
      idUser: [null, Validators.required],
      bufferBeforeMinutes: [0, [Validators.required, Validators.min(0)]],
      bufferAfterMinutes: [0, [Validators.required, Validators.min(0)]],
      minNoticeMinutes: [0, [Validators.required, Validators.min(0)]],

      // Básico
      slug: ['', Validators.maxLength(255)],
      idType: [null],
      title: ['', [Validators.required, Validators.maxLength(200)]],
      description: [''],
      schedulingMode: ['one_on_one'],
      isActive: [true],

      // Scheduling
      maxAdvanceDays: [60],
      maxMeetingsPerDay: [null],
      slotIntervalMinutes: [30],
      defaultDurationMinutes: [30],
      timezone: [''],
      maxInviteesPerSlot: [1],

      // Branding
      logoUrl: [''],
      brandColor: [''],
      darkMode: [false],
      hideBranding: [false],
      defaultLanguage: ['es'],
      supportedLanguages: [''],

      // Video
      videoProvider: [''],
      customVideoUrl: [''],
      phoneNumber: [''],

      // Presencial
      physicalAddress: [''],
      physicalAddressUrl: [''],
      roomName: [''],

      // Pago
      requiresPayment: [false],
      paymentAmount: [null],
      paymentCurrency: ['PEN'],
      stripePriceId: [''],

      // Reschedule
      minCancelNoticeMinutes: [60],
      maxReschedules: [3],

      // Confirmación
      confirmationEmailEnabled: [true],
      followupEmailEnabled: [false],
      followupDelayMinutes: [1440],
      noShowWaitMinutes: [15],
      waitingRoomEnabled: [false],
      waitingRoomMessage: [''],
      defaultAgenda: [''],

      // GDPR
      gdprConsentEnabled: [false],
      gdprConsentText: [''],
      gdprPrivacyUrl: [''],

      // Embed
      embedType: ['standalone'],

      // Pipeline
      autoUpdatePipelineStage: [false],
      idPipelineStageOnBook: [null],
      leadScoreIncrement: [null],
    });

    const user = this.authService.getCurrentUser();
    if (user?.businessId) this.form.patchValue({ idBussines: user.businessId });
  }

  private setupSlugCheck(): void {
    this.slugCheck$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((slug) => {
          if (!slug || slug.trim().length < 3) {
            this.slugAvailable = null;
            return of(null);
          }
          this.slugChecking = true;
          return this.api.checkSlug(slug.trim()).pipe(catchError(() => of(null)));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((available) => {
        this.slugChecking = false;
        this.slugAvailable = available;
      });
  }

  onSlugInput(slug: string): void {
    // En edición, saltar check si el slug no cambió
    this.slugCheck$.next(slug);
  }

  toggleSection(key: string): void {
    this.openSection[key] = !this.openSection[key];
  }

  setTab(tab: BookingTab): void {
    this.activeTab = tab;
  }

  // ─── Color de marca: selector visual + hex editable (INC 52) ───
  /** Valor que consume <input type="color"> (siempre un #rrggbb válido). */
  get brandColorHex(): string {
    const v = this.form.get('brandColor')?.value;
    return typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v) ? v : '#3b82f6';
  }
  onBrandColorPicked(hex: string): void {
    this.form.patchValue({ brandColor: hex });
  }

  /** ¿El código de moneda ya está en el catálogo cargado? (para conservar valor guardado — INC 53). */
  currenciesHasCode(code: string | null | undefined): boolean {
    return !!code && this.currencies.some((c) => c.code === code);
  }

  /** ¿La etapa de pipeline guardada ya está en el catálogo cargado? (INC 55). */
  dealCyclesHasId(id: number | null | undefined): boolean {
    return id != null && this.dealCycles.some((c) => c.id === id);
  }

  /** INC 70 — ¿el estado de reunión guardado ya está en el catálogo cargado? */
  meetingStatusesHasId(id: number | null | undefined): boolean {
    return id != null && this.meetingStatuses.some((s) => s.id === id);
  }

  // ─── Idiomas soportados: selector múltiple ⇆ JSON array (INC 52) ───
  isLanguageSelected(code: string): boolean {
    return this.selectedLanguages.includes(code);
  }
  toggleLanguage(code: string): void {
    this.selectedLanguages = this.selectedLanguages.includes(code)
      ? this.selectedLanguages.filter((c) => c !== code)
      : [...this.selectedLanguages, code];
  }
  private parseSupportedLanguages(raw?: string | null): string[] {
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
    } catch {
      return [];
    }
  }

  // ─── Agenda por defecto: constructor visual ⇆ JSON array (INC 54) ───
  addAgendaItem(): void {
    this.agendaItems = [...this.agendaItems, { title: '', duration: null }];
  }
  removeAgendaItem(index: number): void {
    this.agendaItems = this.agendaItems.filter((_, i) => i !== index);
  }
  private parseAgenda(raw?: string | null): { title: string; duration: number | null }[] {
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return [];
      return arr.map((it: { title?: unknown; duration?: unknown }) => ({
        title: typeof it?.title === 'string' ? it.title : '',
        duration:
          typeof it?.duration === 'number'
            ? it.duration
            : it?.duration != null && !isNaN(+it.duration)
              ? +it.duration
              : null,
      }));
    } catch {
      return [];
    }
  }
  /** INC 77: null (no undefined) cuando no hay ítems — así el PUT de mezcla sí la borra. */
  private serializeAgenda(): string | null {
    const items = this.agendaItems
      .filter((it) => it.title.trim())
      .map((it) => ({ title: it.title.trim(), duration: it.duration ?? 0 }));
    return items.length ? JSON.stringify(items) : null;
  }

  /**
   * Resuelve el nombre del organizador por id contra el directorio de la empresa
   * (accesible a cualquier usuario autenticado). Si el directorio no lo devuelve se muestra
   * un texto legible, nunca el id crudo.
   */
  private resolverOrganizador(idUser: number): void {
    this.userDirectory
      .fetchUsersByIds([idUser])
      .pipe(catchError(() => of([] as DirectoryUser[])))
      .subscribe((users) => {
        const u = users.find((item) => item.id === idUser);
        this.organizerName = u ? this.userLabel(u) : 'Usuario no disponible';
      });
  }

  private cargar(id: number): void {
    this.loading = true;
    this.api.getById(id).subscribe({
      next: (link) => {
        this.form.patchValue(link);
        // Normalizar valores legados de proveedor a los que entiende el backend.
        const legacy: Record<string, string> = { meet: 'google', teams: 'microsoft' };
        const vp = this.form.value.videoProvider;
        if (vp && legacy[vp]) this.form.patchValue({ videoProvider: legacy[vp] });
        // Idiomas soportados: JSON → selección visual (INC 52). Si no hay, usar el idioma por defecto.
        this.selectedLanguages = this.parseSupportedLanguages(link.supportedLanguages);
        if (this.selectedLanguages.length === 0 && link.defaultLanguage) {
          this.selectedLanguages = [link.defaultLanguage];
        }
        // Agenda por defecto: JSON → constructor visual (INC 54).
        this.agendaItems = this.parseAgenda(link.defaultAgenda);
        this.loading = false;
        // Resolver el nombre del organizador para mostrarlo en el buscador.
        if (link.idUser) {
          this.resolverOrganizador(link.idUser);
        }
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el booking link';
        this.loading = false;
      },
    });
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      // INC 78: antes esto era un return mudo. Si el campo inválido vivía en una
      // sección plegada, el usuario pulsaba "Actualizar", no pasaba nada y no
      // aparecía ningún mensaje: parecía que "no se guardan los cambios".
      this.abrirSeccionesConError();
      const faltantes = this.camposInvalidos();
      this.error = `Revisa los campos obligatorios: ${faltantes.join(', ')}.`;
      this.toast.error({ title: 'No se pudo guardar', message: this.error });
      return;
    }

    this.saving = true;
    this.error = '';

    const v = this.form.value;
    const body: MeetingBooksLinkRequest = {
      ...v,
      // INC 77: strings vacíos → null, NO undefined. El PUT tiene semántica de mezcla
      // (INC 75): una clave AUSENTE conserva el valor guardado, así que enviar
      // `undefined` hacía imposible borrar un valor o elegir "Sin video / presencial"
      // (videoProvider ''), y el cambio parecía no guardarse. `null` sí lo borra.
      slug: this.blankToNull(v.slug),
      title: this.blankToNull(v.title),
      description: this.blankToNull(v.description),
      logoUrl: this.blankToNull(v.logoUrl),
      brandColor: this.blankToNull(v.brandColor),
      timezone: this.blankToNull(v.timezone),
      videoProvider: this.blankToNull(v.videoProvider),
      customVideoUrl: this.blankToNull(v.customVideoUrl),
      phoneNumber: this.blankToNull(v.phoneNumber),
      physicalAddress: this.blankToNull(v.physicalAddress),
      physicalAddressUrl: this.blankToNull(v.physicalAddressUrl),
      roomName: this.blankToNull(v.roomName),
      stripePriceId: this.blankToNull(v.stripePriceId),
      waitingRoomMessage: this.blankToNull(v.waitingRoomMessage),
      gdprConsentText: this.blankToNull(v.gdprConsentText),
      gdprPrivacyUrl: this.blankToNull(v.gdprPrivacyUrl),
      // Idiomas soportados: selección visual → JSON array (INC 52).
      supportedLanguages: this.selectedLanguages.length
        ? JSON.stringify(this.selectedLanguages)
        : null,
      // Agenda por defecto: constructor visual → JSON array (INC 54).
      defaultAgenda: this.serializeAgenda(),
    };

    const editando = this.isEditing && this.linkId;
    const op = editando ? this.api.update(this.linkId!, body) : this.api.create(body);

    op.subscribe({
      next: () => {
        this.saving = false;
        // INC 78: confirmación explícita, como en el resto de módulos.
        if (editando) {
          this.toast.updated('Página de reserva');
        } else {
          this.toast.created('Página de reserva');
        }
        this.router.navigate(['/meeting/booking-links']);
      },
      error: (err) => {
        this.saving = false;
        this.error = err.error?.message || 'Error al guardar';
        this.toast.error({ title: 'Error al guardar', message: this.error });
      },
    });
  }

  /** '' / solo espacios → null (borra el valor en el PUT de mezcla); si no, el texto recortado. */
  private blankToNull(value: unknown): string | null {
    if (typeof value !== 'string') return (value as string | null) ?? null;
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  /** INC 78: etiquetas en español de los controles inválidos, para el mensaje de error. */
  private camposInvalidos(): string[] {
    const etiquetas: Record<string, string> = {
      idBussines: 'Empresa',
      idUser: 'Organizador',
      title: 'Título',
      slug: 'Slug',
      bufferBeforeMinutes: 'Margen antes (min)',
      bufferAfterMinutes: 'Margen después (min)',
      minNoticeMinutes: 'Aviso mínimo (min)',
    };
    return Object.keys(this.form.controls)
      .filter((name) => this.form.get(name)?.invalid)
      .map((name) => etiquetas[name] ?? name);
  }

  /** INC 78: despliega las secciones que contienen un campo inválido para que se vea el error. */
  private abrirSeccionesConError(): void {
    const seccionPorCampo: Record<string, string> = {
      idBussines: 'basic',
      idUser: 'basic',
      title: 'basic',
      slug: 'basic',
      bufferBeforeMinutes: 'scheduling',
      bufferAfterMinutes: 'scheduling',
      minNoticeMinutes: 'scheduling',
    };
    for (const [campo, seccion] of Object.entries(seccionPorCampo)) {
      if (this.form.get(campo)?.invalid) this.openSection[seccion] = true;
    }
  }

  cancelar(): void {
    this.router.navigate(['/meeting/booking-links']);
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar página de reserva' : 'Nueva página de reserva';
  }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!c && c.invalid && c.touched;
  }

  getError(field: string): string {
    const c = this.form.get(field);
    if (!c || !c.errors) return '';
    if (c.errors['required']) return 'Requerido';
    if (c.errors['min']) return `Mínimo ${c.errors['min'].min}`;
    if (c.errors['maxlength']) return `Máximo ${c.errors['maxlength'].requiredLength} caracteres`;
    return 'Inválido';
  }
}
